/**
 * The terms prompt for a member carried over from a prior campaign.
 *
 * Both account-creation doors attest as the account is made — the email door
 * through the login modal's checkbox, the Realm door on first join — so a NEW
 * member never sees this. It exists for the one case neither door can reach:
 * someone who joined a PRIOR campaign on this door, is already signed in, and
 * so never opens a login modal again. The server answers
 * `attestationOnFile: false` for exactly those people, and this asks.
 *
 * Server state is the gate — not localStorage. A stored flag survives a terms
 * republish and would silently grandfather exactly the people who must be
 * asked (the pattern is a documented bug source on the Full Throttle hub).
 */
import React, {useState} from "react";
import {Modal} from "@/widgets/Modal";
import {Button} from "@/components/buttons/Button";
import "./DropTermsAcceptModal.scss";

interface DropTermsAcceptModalProps {
    isOpen: boolean;
    dropName: string;
    /** Link to the full rules, authored on the experience. */
    officialRulesUrl?: string;
    isSubmitting?: boolean;
    onAccept: () => void;
    onDismiss: () => void;
}

export const DropTermsAcceptModal: React.FC<DropTermsAcceptModalProps> = ({
    isOpen, dropName, officialRulesUrl, isSubmitting, onAccept, onDismiss,
}) => {
    const [accepted, setAccepted] = useState(false);

    /*
     * Protocol guard — the URL is admin-authored and lands here unvalidated;
     * the https:// check blocks a `javascript:` URI. Same guard the challenge
     * login modal applies to its own official-rules link.
     */
    const rulesHref = officialRulesUrl?.startsWith("https://") ? officialRulesUrl : undefined;

    return (
        <Modal
            isOpen={isOpen}
            toggle={onDismiss}
            size="sm"
            contentClassName="drop-terms-modal"
            ariaLabelledBy="drop-terms-title"
        >
            <h2 id="drop-terms-title" className="drop-terms-modal__title">
                Welcome to {dropName}
            </h2>
            <p className="drop-terms-modal__body">
                Accept the {dropName} rules to keep earning points and appear on the
                leaderboard.
            </p>

            <label className="drop-terms-modal__check" htmlFor="drop-terms-accept">
                <input
                    id="drop-terms-accept"
                    type="checkbox"
                    checked={accepted}
                    onChange={e => setAccepted(e.target.checked)}
                />
                <span>
                    I am 18+ and accept the{" "}
                    {rulesHref
                        ? <a href={rulesHref} target="_blank" rel="noopener noreferrer">
                            official rules
                        </a>
                        : "official rules"}
                    .
                </span>
            </label>

            <div className="drop-terms-modal__actions">
                <Button variant="ghost" onClick={onDismiss}>
                    Not now
                </Button>
                <Button
                    variant="primary"
                    onClick={onAccept}
                    disabled={!accepted}
                    loading={isSubmitting}
                    loadingText="Saving…"
                >
                    Accept &amp; continue
                </Button>
            </div>

            {/* Declining is allowed, but it must not be a silent dead end. */}
            <p className="drop-terms-modal__note">
                Until you accept, you won&apos;t earn points or appear on the leaderboard.
            </p>
        </Modal>
    );
};
